import DBStore from "@/core/db_store";
import Swal from "sweetalert2";
import { computed, h, nextTick, reactive, ref, watch } from "vue";
import { edit } from "@/core/db";
import { openPanel } from "@/layouts/layouts";
import { getModel } from "@/tables";
import { genGUID, isAsync, deepMerge } from "@/helpers/utils";
import Export from "./Export.vue";
import { tableExport } from "@/api/db";
import { query } from "@/api/db";
import Bus from "@/core/bus";
import api from "@/core/api";

export default class DBGridController {
  public store: DBStore;
  public form: any;
  public field: any;
  public verified: any;
  public valid: any;
  public feedback: any;
  public readonly: any;
  public access: any;
  public settings: any;
  public panelFun: any;
  public standardCommands: any;
  public pages: any;
  public page: any;
  public limit: any;
  public searchText: any;
  public stateField: any;
  public rows: any;
  public sortingFields: any;
  public footerData: any;
  public sortedFields: any;
  /** Не дергать fetch/save при программной подстановке limit */
  private _suspendLimitWatch = false;

  constructor(protected props: any, protected emit: any) {
    this.verified = ref(false);
    this.valid = ref(false);
    this.feedback = ref("");
    this.searchText = ref("");
    this.pages = ref(0);
    this.page = ref(0);
    this.panelFun = ref([]);
    this.rows = ref([]);
    this.sortingFields = reactive({});

    this.footerData = reactive({});

    this.form = props.form;
    this.field = props.field;

    this.store = props.store ? props.store : this.createStore();

    if (!this.store) {
      // Создаем заглушку для store если не удалось создать
      this.store = {
        model: {
          access: { read: false, create: false, update: false, delete: false },
        },
        data: { rows: [], position: -1, select: [] },
        state: { readonly: false },
        fetchOptions: {
          page: 0,
          limit: 25,
          searchtext: "",
          filters: [],
          orderby: [],
        },
        pages: 0,
        name: "dummy",
        fetchData: () => Promise.resolve(),
        accessCreate: () => false,
        accessRead: () => false,
        accessUpdate: () => false,
        accessDelete: () => false,
        currentData: () => null,
        setPosition: () => {},
        selectRow: () => {},
        deleteRecord: () => Promise.resolve(),
        refreshData: () => Promise.resolve(),
        createState: () => {},
        availabeRole: () => false,
        getValue: () => null,
        setValue: () => null,
        files: {},
      } as any;
    }

    if (!this.store?.model?.access) {
      this.store.model.access = {
        read: false,
        create: false,
        update: false,
        delete: false,
      };
    }

    this.access = this.store.model.access;

    this.readonly = computed(() => {
      return !(
        this.access.create ||
        this.access.update ||
        this.access.delete
      ) ||
        props.readonly ||
        this.store.state.readonly ||
        props?.form?.readonly?.value
        ? true
        : false;
    });

    // Вычисляемое свойство для сортировки полей по order
    this.sortedFields = computed(() => {
      const fields = this.store.state.fields;
      if (!fields) return [];

      const fieldKeys = Object.keys(fields);

      // Сортируем ключи по order
      fieldKeys.sort((a, b) => {
        const orderA = fields[a]?.config?.order ?? fieldKeys.indexOf(a);
        const orderB = fields[b]?.config?.order ?? fieldKeys.indexOf(b);
        return orderA - orderB;
      });

      return fieldKeys;
    });

    this.settings = reactive({
      id: null,
      filters: [],
      orderBy: {},
      menu: true,
      numbering: false,
      sorting: false,
      slectMode: false,
      searchpanel: true,
      searchAway: true,
      limits: [25, 50, 75, 100, 1000, 3000],
      limit: 25,
      owner: null, //id - зависимого поля
      panelFun: true,
      footer: false,
      actions: {
        view: false,
        edit: true,
        create: true,
        copy: true,
        delete: true,
      },
      contextMenuBody: [],
      contextMenuHead: [],
      default: {},
      localStorageKey: null,
      edit: {}, //Настройки компонента редактирования
      height: null,
      autoOpen: true,
      deleteText: "Удалить запись?",
      crossot: () => false, //Перечеркивание
      export: true,
      transparentTr: false,
      styleTr: () => ({}),
      classTr: () => ({}),
      rowSelect: false,
      onCreate: () => null,
      onRowSelect: (data: any, checked: boolean) => {
        this.store.selectRow(data, checked);
      },
      onBeforeNew: async (params: any) => await edit(params),
      onBeforeEdit: async (params: any) => await edit(params),
      onBeforeCopy: async (params: any) => await edit(params),
      onKeyDown: async () => true,
      cellClick: {},
      settingsKey: null, // Уникальный ключ для сохранения настроек таблицы на бэкенде
    });

    Object.assign(this.settings, props.config);

    // Режим выбора записи: двойной клик / Enter — выбрать, не Edit
    if (this.settings.slectMode) {
      if (this.store?.state?.fields?.id?.config) {
        this.store.state.fields.id.config.visible = false;
      }
    }

    // Применяем конфигурацию из field.config для подтаблиц
    if (this.field && this.form && this.form.store) {
      try {
        const field = this.getFieldModel(
          this.field.split("."),
          this.form.store.state,
        );
        if (field?.config) {
          Object.assign(this.settings, field.config);
        }
      } catch (e) {
        // Игнорируем ошибки, если поле не найдено
      }
    }

    this.limit = ref(this.settings.limit);

    // Локальная пагинация для вложенных таблиц (subtable),
    // у которых включен allowSubtablePagination
    const updateLocalPages = () => {
      if (
        this.settings.allowSubtablePagination &&
        this.store?.model?.subtable
      ) {
        const rowsCount = this.store?.data?.rows?.length || 0;
        const limit = this.limit.value || rowsCount || 1;
        this.pages.value = rowsCount > limit ? Math.ceil(rowsCount / limit) : 1;
      }
    };

    if (Object.keys(this.settings.orderBy).length > 0) {
      Object.assign(this.sortingFields, this.settings.orderBy);

      this.onSort(this.settings.orderBy, false);
    }

    this.standardCommands = {
      edit: {
        caption: "Изменить",
        title: "Изменить",
        icon: <i class="icon icon-edit"></i>,
        class: "btn btn-edit",
        onClick: () => this.editRow(),
      },
      view: {
        caption: "Просмотр",
        title: "Просмотр",
        icon: <i class="icon icon-eye"></i>,
        class: "btn btn-edit",
        onClick: () => this.editRow(),
      },
      add: {
        caption: "Добавить",
        title: "Добавить",
        icon: <i class="icon icon-plus-circle"></i>,
        class: "btn btn-add",
        onClick: () => this.addRow(),
      },
      copy: {
        caption: "Копировать",
        title: "Копировать",
        icon: <i class="icon icon-file"></i>,
        class: "btn btn-copy",
        onClick: () => this.copyRow(),
      },
      delete: {
        caption: "Удалить",
        title: "Удалить",
        icon: <i class="icon icon-x-circle"></i>,
        class: "btn btn-delete",
        // onClick: () => this.deleteRow()
        onClick: () => this.deleteSelected(),
      },
    };

    nextTick(() => {
      if (this.readonly.value) {
        this.createPanelFun([
          {
            view: this.settings.view ? {} : null,
          },
        ]);
      } else {
        this.createPanelFun([
          {
            edit: this.access.update && this.settings.actions.edit ? {} : null,
            add: this.access.create && this.settings.actions.create ? {} : null,
            copy: this.access.create && this.settings.actions.copy ? {} : null,
            delete:
              this.access.delete && this.settings.actions.delete ? {} : null,
          },
        ]);
      }
    });

    // Когда подгружаются / меняются строки во вложенной таблице,
    // пересчитываем количество страниц для локальной пагинации
    watch(
      () => this.store?.data?.rows?.length,
      () => {
        updateLocalPages();
      },
    );

    watch(
      () => this.searchText.value,
      async () => await this.fetchData({ page: 0 }),
    );

    watch(
      () => this.limit.value,
      async () => {
        if (this._suspendLimitWatch) return;

        const next = Number(this.limit.value);
        if (Number.isFinite(next) && next > 0) {
          this.settings.limit = next;
          if (this.limit.value !== next) {
            this._suspendLimitWatch = true;
            this.limit.value = next;
            this._suspendLimitWatch = false;
          }
        }

        if (
          this.settings.allowSubtablePagination &&
          this.store?.model?.subtable
        ) {
          // При смене лимита просто пересчитываем количество страниц и остаёмся на первой
          this.page.value = 0;
          updateLocalPages();
        } else {
          await this.fetchData({ page: 0 });
        }

        this.saveColumnSettings();
      },
    );
  }

  /** Применить сохранённый limit без повторного fetch через watch */
  applySavedLimit(limit: unknown) {
    const next = Number(limit);
    if (!Number.isFinite(next) || next <= 0) return;
    if (
      Array.isArray(this.settings.limits) &&
      !this.settings.limits.includes(next)
    ) {
      return;
    }
    this._suspendLimitWatch = true;
    this.settings.limit = next;
    this.limit.value = next;
    this._suspendLimitWatch = false;
  }

  getFieldModel(fields: any, structure: any): any {
    const field: any = fields.shift();

    if (fields.length > 0) {
      return this.getFieldModel(fields, structure.fields[field].model);
    } else {
      return structure.fields[field];
    }
  }

  createStore() {
    if (!this.field || typeof this.field !== "string") {
      return null;
    }

    const field = deepMerge(
      {},
      this.getFieldModel(this.field.split("."), this.form.store.state),
    );

    const store = new DBStore(
      field?.table ? field.table : `table_${this.field}`,
    );

    if (field?.type?.table) {
      store.model = { ...field.type.table, subtable: true };
    } else {
      return null;
    }

    // Используем field напрямую для createState, так как он уже содержит всю конфигурацию
    // включая fields и config
    this.stateField = field;

    store.createState(this.stateField);

    if (this.stateField.depends && this.form.store.data) {
      const ownerId = this.form.store.data[this.stateField.depends];
      if (ownerId) {
        store.owner = ownerId;
      }
    }

    const access = this.form.store.model.access
      ? this.form.store.model.access
      : {
          read: true,
          create: true,
          update: true,
          delete: true,
        };

    const editable =
      "access" in this.stateField
        ? store.availabeRole(this.stateField.access, this.field, field)
        : access.create || access.update || access.delete;

    store.model.access = {
      read: true,
      create: editable,
      update: editable,
      delete: editable,
    };

    if (field.depends) {
      store.model.depends = field.depends;

      store.owner = this.form.store.getValue(field.depends);

      if (!store.owner) {
        store.owner = this.form.store.setValue(field.depends, genGUID());
      }
    }

    const data = this.form.store.getValue(this.field);

    if (data) {
      store.data.rows = data;
      store.data.position = data.length ? 0 : -1;
    } else {
      const newData = this.form.store.setValue(this.field, []);

      store.data.rows = newData;
      store.data.position = newData.length ? 0 : -1;
    }

    store.files = this.form.store.files;
    // чтобы добавление/правка/удаление строки в гриде помечало родителя dirty
    store.parentRecordStore = this.form.store;

    // Грид — источник правды по табличной части; beforeSave читает отсюда,
    // если data.field когда-то подменили новым массивом (filter → новая ссылка).
    if (!this.form.store.nestedStores) this.form.store.nestedStores = {};
    this.form.store.nestedStores[this.field] = store;

    watch(
      () => this.form.store.data?.[this.field],
      (rows) => {
        if (Array.isArray(rows) && store.data.rows !== rows) {
          store.data.rows = rows;
          store.data.position = rows.length ? 0 : -1;
        }
      },
    );

    return store;
  }

  createPanelFun(items: any) {
    this.panelFun.value.splice(0, this.panelFun.value.length);

    for (const item of items) {
      const panel: any = {};

      for (const key in item) {
        if (item[key]) {
          panel[key] = {};

          if (key in this.standardCommands) {
            Object.assign(panel[key], this.standardCommands[key], item[key]);
          } else {
            Object.assign(panel[key], item[key]);
          }

          if (typeof panel[key].onClick === "string") {
            const originalOnClick = panel[key].onClick;
            panel[key].onClick = () => this.emit(originalOnClick);
          }
        }
      }

      this.panelFun.value.push(panel);
    }
  }

  validation() {
    if (this.form && this.field) {
      const field = this.form.store.state.fields[this.field];

      if (this.props.validation && field.validation) {
        for (const i in field.validation) {
          const result = field.validation[i](this.store.data.rows);
          if (result !== false) {
            this.verified.value = true;
            this.valid.value = false;
            this.feedback.value = result;

            return result;
          }
        }
      }
    }

    this.verified.value = false;
    this.valid.value = true;
    this.feedback.value = "";

    return false;
  }

  home() {
    this.activeRow(0);
  }

  end() {
    this.activeRow(this.store.data.rows.length - 1);
  }

  focusRow() {
    const position = this.store.data.position;
    if (
      position != -1 &&
      this.store.data.rows.length > position &&
      this.rows.value[position]
    ) {
      this.rows.value[position].focus();
    }
  }

  activeRow(position: number) {
    this.store.setPosition(position);

    this.focusRow();

    this.emit("active", this.store.data.rows[position]);
  }

  positionsUpdate() {
    const model = this.store.model;

    if (model.subtable && model.positionField) {
      const rows = this.store.data.rows;

      for (const postion in rows) {
        rows[postion][model.positionField] = Number(postion) + 1;
      }
    }
  }

  async addRow(newData = {}) {
    if (!this.readonly.value && this.access.create) {
      const data = {
        ...(typeof this.settings.default == "function"
          ? isAsync(this.settings.default)
            ? await this.settings.default()
            : this.settings.default()
          : this.settings.default),
        ...newData,
      };

      const response: any = await query({
        table: this.store.name,
        method: "new",
        data,
      });

      const params = Object.assign({
        store: this.store,
        data: response.complete ? response.data : data,
        config: this.settings.edit,
      });

      if (this.store.model.ownerField) {
        if (this.store.owner) {
          params.data.owner = this.store.owner;
        } else {
          return;
        }
      }

      await this.settings.onBeforeNew(params);

      this.positionsUpdate();

      this.focusRow();

      this.verified.value && this.validation();
    }
  }

  async copyRow(defaults = {}) {
    //Возвращать фокус на грид
    const data = this.store.currentData();
    if (data) {
      if (!this.readonly.value && this.access.create) {
        await this.settings.onBeforeCopy({
          copy: true,
          store: this.store,
          data,
          config: this.settings.edit,
          defaults,
        });

        this.positionsUpdate();

        this.verified.value && this.validation();

        this.focusRow();
      }
    }
  }

  async openRow(data: any) {
    const params: any = Object.assign({
      store: this.store,
      data,
      config: this.settings.edit,
    });

    if (this.store.model.ownerField) {
      if (this.store.owner) {
        params.data.owner = this.store.owner;
      } else {
        return;
      }
    }

    if (this.settings.actions.view || this.readonly.value) {
      params.readonly = true;

      await this.settings.onBeforeEdit(params);

      this.positionsUpdate();

      this.focusRow();
    } else {
      if (!this.readonly.value && this.access.update) {
        params.owner = params.store.owner;

        await this.settings.onBeforeEdit(params);

        this.positionsUpdate();

        this.focusRow();

        this.verified.value && this.validation();
      }
    }
  }

  async editRow() {
    //Возвращать фокус на грид
    const data = this.store.currentData();

    if (data) this.openRow(data);
  }

  getSelected() {
    const result: any[] = [];

    if (this.store.data.select.length > 0) {
      this.store.data.select.forEach((el: any) => result.push(el));
    } else {
      if (this.store.currentData()) {
        result.push(this.store.currentData().id);
      }
    }
    return result;
  }
  async deleteSelected() {
    if (!this.readonly.value && this.access.delete) {
      const selectedItems = this.getSelected();
      if (selectedItems.length === 0) {
        return;
      }

      const data = this.store.data.rows.find(
        (el: any) => el.id === selectedItems[0],
      );
      if (!this.store?.model || (await this.store.model.onBeforeDelete(data))) {
        Swal.fire({
          title: `Удалить запись?`,
          showCancelButton: true,
          confirmButtonText: "Да",
          cancelButtonText: "Отмена",
        }).then(async (result) => {
          if (result.isConfirmed) {
            for (const id of selectedItems) {
              const row = this.store.data.rows.find((el: any) => el.id === id);
              if (row) {
                if (
                  !this.store.model ||
                  (await this.store.model.onBeforeDelete(row))
                ) {
                  await this.store.deleteRecord(row);

                  this.store?.model &&
                    (await this.store.model.onAfterDelete(row));
                }
              }
            }

            // Обновляем отображение
            this.positionsUpdate();
            this.verified.value && this.validation();
            this.focusRow();

            // Отправляем событие для обновления данных
            Bus.$emit("refresh-data", { table: this.store.name });
          }
        });
      }
    }
  }

  async deleteRow() {
    if (!this.readonly.value && this.access.delete) {
      const data = this.store.currentData();
      if (data) {
        if (
          !this.store?.model ||
          (await this.store.model.onBeforeDelete(data))
        ) {
          Swal.fire({
            title: this.settings.deleteText,
            showCancelButton: true,
            confirmButtonText: "Да",
            cancelButtonText: "Отмена",
          }).then(async ({ value }) => {
            if (value) {
              await this.store.deleteRecord(data);

              this.positionsUpdate();

              this.store?.model && (await this.store.model.onAfterDelete(data));

              // Отправляем событие для обновления данных
              Bus.$emit("refresh-data", { table: this.store.name });

              this.verified.value && this.validation();

              this.focusRow();
            }
          });
        }
      }
    }
  }

  selectRow(data: any = this.store.currentData()) {
    if (this.settings.slectMode) {
      this.emit("select", data);
      return;
    }
    this.editRow();
  }

  setFields(fields: any = []) {
    const config: any = {};

    if (fields.length > 0) {
      config.fields = {};

      for (const field of fields) {
        if (this.store.state.fields[field.name]) {
          config.fields[field.name] = Object.assign(
            {},
            this.store.state.fields[field.name]
              ? this.store.state.fields[field.name]
              : {},
          );

          Object.assign(config.fields[field.name], {
            description: field.description,
          });

          config.fields[field.name].config.visible = field.visible;

          // Сохраняем order из позиции в массиве
          if (field.order !== undefined) {
            config.fields[field.name].config.order = field.order;
          }
        }
      }
    } else {
      //При сбросе настроек — берём поля из конфига (props.config или field.config для подтаблиц)
      let _fields = this.props.config?.fields;

      // Для подтаблиц конфиг полей приходит через field.config, а не props.config
      if (!_fields && this.field && this.form?.store?.state) {
        try {
          const fieldModel = this.getFieldModel(
            this.field.split("."),
            this.form.store.state,
          );
          _fields = fieldModel?.config?.fields;
        } catch (e) {}
      }

      if (_fields) {
        config.fields = deepMerge({}, _fields);
      } else {
        const schema = getModel(this.store.name);
        if (schema) {
          // Фолбэк — если нет конфига, берём из модели
        }
      }
    }

    this.store.createState(config);
  }

  contextMenuHead() {
    const items: any[] = [];

    if (!this.store.model.subtable) {
      for (const key in this.store.state.fields) {
        const field = this.store.state.fields[key];
        if (field?.config?.sorting) {
          items.push({
            icon: <i class="icon icon-arrow-loop"></i>,
            caption: "Сброс сортировки",
            onClick: async () => await this.resetSorting(),
          });

          break;
        }
      }

      items.push({
        icon: <i class="icon icon-arrow-sync"></i>,
        caption: "Обновить",
        onClick: async () => await this.fetchData(),
      });
    }

    // Show table settings menu if we have a settings key (from settingsKey or store.name)
    const settingsKey = this.getSettingsKey();
    if (this.settings.localStorageKey || settingsKey) {
      items.push({
        icon: <i class="icon icon-cog"></i>,
        caption: "Настройка таблицы",
        onClick: async () => {
          openPanel({
            scrollable: true,
            modal: true,
            caption: "Настройка таблицы",
            onCreate: (panel: any) => ({
              component: h(Export, { fields: this.store.state.fields }),
              buttons: [
                {
                  class: "btn btn-action",
                  caption: "Сохранить",
                  onClick: async () => {
                    const config = panel.ref.getFields();

                    // Сохраняем через saveColumnSettings (поддерживает и бэкенд, и localStorage)
                    this.setFields(config);
                    await this.saveColumnSettings();

                    panel.close();
                  },
                },
                ...(!this.settings.noResetSettings
                  ? [
                      {
                        class: "btn btn-action",
                        caption: "Сброс настроек",
                        onClick: async () => {
                          // Удаляем настройки из localStorage
                          if (this.settings.localStorageKey) {
                            localStorage.removeItem(
                              this.settings.localStorageKey,
                            );
                          }

                          // Удаляем настройки с бэкенда
                          if (settingsKey) {
                            try {
                              const { tableSettingsApi } = await import(
                                "@/api/tableSettings"
                              );
                              await tableSettingsApi.deleteSettings(
                                settingsKey,
                              );
                            } catch (error) {
                              console.error(
                                "Failed to delete settings from backend:",
                                error,
                              );
                            }
                          }

                          this.setFields();

                          panel.close();
                        },
                      },
                    ]
                  : []),
              ],
            }),
          });
        },
      });
    }

    if (this.settings.export) {
      const isSubtable = !!this.store.model.subtable;
      items.push({
        icon: <i class="icon icon-export"></i>,
        caption: "Экспорт таблицы",
        onClick: async () => {
          if (isSubtable) {
            // Для подтаблиц — экспорт только видимых полей без диалога
            const rows = this.store.data.rows || [];
            if (!rows.length) return;

            const exportFields: any[] = [];
            const fieldConfigs: any = {};
            for (const key in this.store.state.fields) {
              const f = this.store.state.fields[key];
              const vis =
                typeof f.config.visible === "function"
                  ? f.config.visible()
                  : f.config.visible;
              if (vis && !f.config.hide) {
                exportFields.push({
                  name: key,
                  description: f.description || key,
                  visible: true,
                });
                fieldConfigs[key] = f;
              }
            }

            // Подготовка строк с display-значениями (как в гриде)
            const dayjs = (await import("dayjs")).default;
            const processedRows = rows.map((row: any) => {
              const processed: any = {};
              for (const ef of exportFields) {
                const structure = fieldConfigs[ef.name];
                let value: any = "";

                // 1. calc
                if (structure?.config?.calc) {
                  value =
                    typeof structure.config.calc === "function"
                      ? structure.config.calc(row)
                      : structure.config.calc;
                }
                // 2. render
                else if (typeof structure?.config?.render === "function") {
                  const rendered = structure.config.render(row[ef.name], row);
                  value =
                    rendered !== undefined
                      ? rendered
                      : row[`_${ef.name}`] || row[ef.name];
                }
                // 3. display
                else if (typeof structure?.config?.display === "function") {
                  value = structure.config.display(row[ef.name], row);
                }
                // 4. _fieldname (справочники)
                else if (
                  row[`_${ef.name}`] !== undefined &&
                  row[`_${ef.name}`] !== null
                ) {
                  value = row[`_${ef.name}`];
                }
                // 5. enum
                else if (
                  typeof structure?.type === "object" &&
                  structure.type.enum
                ) {
                  const enumItem = structure.type.enum.find(
                    (e: any) => e.id == row[ef.name],
                  );
                  value = enumItem ? enumItem.name : row[ef.name];
                }
                // 6. Форматирование типов
                else {
                  const raw = row[ef.name];
                  if (raw === null || raw === undefined) {
                    value = "";
                  } else if (structure?.type === "DATEONLY") {
                    value =
                      raw && raw !== "0000-00-00"
                        ? dayjs(raw).format("DD.MM.YYYY")
                        : "";
                  } else if (structure?.type === "DATE") {
                    value = raw ? dayjs(raw).format("DD.MM.YYYY HH:mm:ss") : "";
                  } else if (structure?.type === "BOOLEAN") {
                    value = raw ? "Да" : "Нет";
                  } else {
                    value = raw;
                  }
                }

                processed[ef.name] =
                  value !== null && value !== undefined ? value : "";
              }
              return processed;
            });

            try {
              const data: any = await api.query("/db/export-data", {
                method: "POST",
                data: {
                  rows: processedRows,
                  fields: exportFields,
                  sheetName: this.store.model.description || "Экспорт",
                },
                responseType: "blob",
              });
              const blob = new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              });
              const link = document.createElement("a");
              link.download = "export.xlsx";
              link.href = URL.createObjectURL(blob);
              link.click();
              URL.revokeObjectURL(link.href);
            } catch (e) {
              console.error("Export error:", e);
            }
          } else {
            // Для обычных таблиц — диалог выбора полей
            openPanel({
              modal: true,
              scrollable: true,
              caption: "Экспорт таблицы",
              onCreate: (panel: any) => ({
                component: h(Export, { fields: this.store.state.fields }),
                buttons: [
                  {
                    class: "btn btn-action",
                    caption: "Экспорт",
                    onClick: async () => {
                      const options = {
                        fields: panel.ref.getFields(),
                        options: this.store.fetchParams(),
                      };

                      const data: any = await tableExport(
                        this.store.name,
                        options,
                      );
                      const blob = new Blob([data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                      });

                      const link = document.createElement("a");

                      link.download = "export.xlsx";

                      link.href = URL.createObjectURL(blob);

                      link.click();

                      URL.revokeObjectURL(link.href);

                      panel.close();
                    },
                  },
                ],
              }),
            });
          }
        },
      });
    }

    return items.concat(
      typeof this.settings.contextMenuHead == "function"
        ? this.settings.contextMenuHead()
        : this.settings.contextMenuHead,
    );
  }

  contextMenuBody(data: any) {
    const items: any[] = [];

    if (this.readonly.value || this.settings.actions.view) {
      items.push({
        icon: <i class="icon icon-eye"></i>,
        caption: "Просмотр",
        onClick: () => this.editRow(),
      });
    } else {
      if (this.settings.actions.edit && this.access.update)
        items.push({
          icon: <i class="icon icon-edit"></i>,
          caption: "Изменить",
          onClick: () => this.editRow(),
        });

      if (this.access.create) {
        if (this.settings.actions.create) {
          items.push({
            icon: <i class="icon icon-plus-circle"></i>,
            caption: "Добавить",
            onClick: () => this.addRow(),
          });
        }

        if (this.settings.actions.copy) {
          items.push({
            icon: <i class="icon icon-file"></i>,
            caption: "Копировать",
            onClick: () => this.copyRow(),
          });
        }
      }

      if (this.settings.actions.delete && this.access.delete)
        items.push({
          icon: <i class="icon icon-x-circle"></i>,
          caption: "Удалить",
          // onClick: () => this.deleteRow()
          onClick: () => this.deleteSelected(),
        });
    }

    return items.concat(
      typeof this.settings.contextMenuBody == "function"
        ? this.settings.contextMenuBody(data)
        : this.settings.contextMenuBody,
    );
  }

  async onKeyDown(event: any) {
    const rowData: any = this.store.data.rows[this.store.data.position]
      ? this.store.data.rows[this.store.data.position]
      : {};

    if (await this.settings.onKeyDown(event, rowData, this)) {
      switch (event.keyCode) {
        case 13: //enter
          this.selectRow();

          break;

        case 32: //space
          if (this.settings.rowSelect && this.store.data.position != null) {
            event.preventDefault();

            const id = rowData.id;

            this.settings.onRowSelect(
              rowData,
              !this.store.data.select.includes(id),
            );
          }

          break;

        case 45: //insert
          if (this.settings.actions.create) this.addRow();

          break;

        case 46: //delete
          if (this.settings.actions.delete)
            // this.deleteRow();
            this.deleteSelected();

          break;

        case 113: //F2
          if (this.settings.actions.edit) this.editRow();

          break;

        case 37: //left
          event.preventDefault();

          if (event.ctrlKey) {
            if (this.page.value > 0)
              await this.fetchData({ page: this.page.value - 1 });
          } else this.home();

          this.focusRow();

          break;

        case 39: //right
          event.preventDefault();

          if (event.ctrlKey) {
            if (this.page.value < this.pages.value - 1)
              await this.fetchData({ page: this.page.value + 1 });
          } else this.end();

          this.focusRow();

          break;

        case 38: //up
          event.preventDefault();

          if (
            this.store.data.position != null &&
            this.store.data.position > 0
          ) {
            if (event.ctrlKey) {
              this.rowUp();
            } else {
              this.activeRow(this.store.data.position - 1);
            }

            this.focusRow();
          }
          break;

        case 40: //down
          event.preventDefault();

          if (
            this.store.data.position != null &&
            this.store.data.position < this.store.data.rows.length - 1
          ) {
            if (event.ctrlKey) {
              this.rowDown();
            } else {
              this.activeRow(this.store.data.position + 1);
            }

            this.focusRow();
          }

          break;

        case 36: //home
          event.preventDefault();

          if (event.ctrlKey) {
            await this.fetchData({ page: 0 });
          } else {
            this.home();
          }

          this.focusRow();

          break;

        case 35: //end
          event.preventDefault();

          if (event.ctrlKey && this.pages.value > 0) {
            await this.fetchData({ page: this.pages.value - 1 });
          } else {
            this.end();
          }

          this.focusRow();

          break;
      }
    }
  }

  async onSort(sort: any, fetch = true) {
    this.store.fetchOptions.orderby = [];

    for (const key in sort) {
      const field: any = this.store.state.fields[key];

      if (field && field.config && field.config.sorting) {
        this.store.fetchOptions.orderby.push(
          field.config.sorting + (sort[key] ? "" : " desc"),
        );
      }
    }

    // Клиентская сортировка только для таблиц, где ВСЕ данные загружены локально
    // (offLine или явно указано clientSort в настройках).
    // Для подтаблиц (subtable) всегда используем серверную сортировку,
    // чтобы данные корректно синхронизировались с родительской формой при сохранении.
    // Для обычных пагинированных таблиц сортировка идёт через сервер (fetchData).
    if (
      this.store.data.rows &&
      this.store.data.rows.length > 0 &&
      (this.store.model.offLine || this.settings?.clientSort)
    ) {
      const rows = [...this.store.data.rows];
      rows.sort((a: any, b: any) => {
        for (const key in sort) {
          const field: any = this.store.state.fields[key];
          if (field && field.config && field.config.sorting) {
            let aVal = a[key];
            let bVal = b[key];

            // Обработка null/undefined
            if (aVal == null) aVal = "";
            if (bVal == null) bVal = "";

            // Для дат используем сравнение дат (проверяем строки)
            if (
              typeof aVal === "string" &&
              typeof bVal === "string" &&
              (aVal.match(/^\d{4}-\d{2}-\d{2}/) ||
                bVal.match(/^\d{4}-\d{2}-\d{2}/))
            ) {
              aVal = new Date(aVal).getTime() || 0;
              bVal = new Date(bVal).getTime() || 0;
            }
            // Для числовых полей используем числовое сравнение
            else if (
              typeof aVal === "number" ||
              typeof bVal === "number" ||
              (typeof aVal === "string" &&
                !isNaN(parseFloat(aVal)) &&
                !isNaN(parseFloat(bVal)))
            ) {
              aVal = parseFloat(String(aVal)) || 0;
              bVal = parseFloat(String(bVal)) || 0;
            }
            // Для строк используем строковое сравнение
            else {
              aVal = String(aVal || "").toLowerCase();
              bVal = String(bVal || "").toLowerCase();
            }

            // Сравнение с учетом направления сортировки
            if (aVal < bVal) return sort[key] ? -1 : 1;
            if (aVal > bVal) return sort[key] ? 1 : -1;
          }
        }
        return 0;
      });
      this.store.data.rows = rows;
      return;
    }

    if (fetch) await this.fetchData();
  }

  async sort(sort: any, fetch = true) {
    for (const key in this.sortingFields) {
      delete this.sortingFields[key];
    }

    Object.assign(this.sortingFields, sort);

    this.onSort(this.sortingFields, fetch);
  }

  async selectPage(page: any) {
    // Для локально пагинируемых вложенных таблиц просто меняем номер страницы,
    // данные уже загружены целиком в store.data.rows
    if (this.settings.allowSubtablePagination && this.store?.model?.subtable) {
      this.page.value = page;
      return;
    }

    await this.fetchData({ page });
  }

  sortByPosition() {
    const model = this.store.model;

    if (model.subtable && model.positionField) {
      const { rows } = this.store.data;

      rows.sort(
        (a: any, b: any) => a[model.positionField] - b[model.positionField],
      );
    }
  }

  rowUp() {
    this.positionsUpdate();

    const model = this.store.model;

    if (model.subtable && model.positionField) {
      const { position, rows } = this.store.data;

      const posA = rows[position][model.positionField];
      const posB = rows[position - 1][model.positionField];

      rows[position][model.positionField] = posB;
      rows[position - 1][model.positionField] = posA;

      this.store.setPosition(position - 1);

      setTimeout(() => this.focusRow(), 0);
    }

    this.sortByPosition();
  }

  rowDown() {
    this.positionsUpdate();

    const model = this.store.model;

    if (model.subtable && model.positionField) {
      const { position, rows } = this.store.data;

      const posA = rows[position][model.positionField];
      const posB = rows[position + 1][model.positionField];

      rows[position][model.positionField] = posB;
      rows[position + 1][model.positionField] = posA;

      this.store.setPosition(position + 1);

      setTimeout(() => this.focusRow(), 0);
    }

    this.sortByPosition();
  }

  async resetSorting() {
    this.store.fetchOptions.orderby.splice(
      0,
      this.store.fetchOptions.orderby.length,
    );

    for (const key in this.sortingFields) {
      delete this.sortingFields[key];
    }

    if (Object.keys(this.settings.orderBy).length > 0) {
      Object.assign(this.sortingFields, this.settings.orderBy);

      this.onSort(this.settings.orderBy, false);
    }

    await this.fetchData();
  }

  async fetchData(options: any = {}) {
    if ("page" in options) {
      this.page.value = options.page;
    }

    // Текущая страница UI — источник истины; не сбрасывать при silent-refresh
    this.store.fetchOptions.page = this.page.value;
    this.store.fetchOptions.searchtext = this.searchText.value;
    this.store.fetchOptions.limit = this.limit.value;

    await this.store.fetchData(options);

    this.pages.value = this.store.pages;
  }

  /**
   * Update column width
   * @param fieldKey - Field key
   * @param width - New width in pixels
   */
  updateColumnWidth(fieldKey: string, width: number) {
    const field = this.store.state.fields[fieldKey];
    if (field && field.config) {
      // Сохраняем ширину как строку с "px" для согласованности с GridHead.vue
      field.config.width = `${width}px`;
      this.saveColumnSettings();
    }
  }

  /**
   * Reorder columns by moving one column before/after another
   * @param fromKey - Key of column to move
   * @param toKey - Key of target column
   * @param position - 'before' or 'after'
   */
  reorderColumns(fromKey: string, toKey: string, position: "before" | "after") {
    const fields = this.store.state.fields;
    const fieldKeys = Object.keys(fields);

    const fromIndex = fieldKeys.indexOf(fromKey);
    const toIndex = fieldKeys.indexOf(toKey);

    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
      return;
    }

    // Get order values
    const orders = fieldKeys.map((key, index) => ({
      key,
      order: fields[key].config?.order ?? index,
    }));

    // Sort by current order
    orders.sort((a, b) => a.order - b.order);

    // Find indices in sorted array
    const sortedFromIndex = orders.findIndex((o) => o.key === fromKey);
    const sortedToIndex = orders.findIndex((o) => o.key === toKey);

    if (sortedFromIndex === -1 || sortedToIndex === -1) {
      return;
    }

    // Calculate new order value
    let newOrder: number;
    if (position === "before") {
      if (sortedToIndex === 0) {
        newOrder = orders[0].order - 1;
      } else {
        const prevOrder = orders[sortedToIndex - 1].order;
        newOrder = (prevOrder + orders[sortedToIndex].order) / 2;
      }
    } else {
      if (sortedToIndex === orders.length - 1) {
        newOrder = orders[orders.length - 1].order + 1;
      } else {
        const nextOrder = orders[sortedToIndex + 1].order;
        newOrder = (orders[sortedToIndex].order + nextOrder) / 2;
      }
    }

    // Update order for moved field
    if (fields[fromKey].config) {
      fields[fromKey].config.order = newOrder;
    }

    this.saveColumnSettings();
  }

  /**
   * Get the settings key for this table
   * Uses settingsKey if provided, otherwise falls back to store.name
   */
  getSettingsKey(): string | null {
    return this.settings.settingsKey || this.store?.name || null;
  }

  /**
   * Serialize field visibility for persistence (skip functions — they are runtime-only).
   */
  private serializeVisible(visible: unknown): boolean | undefined {
    if (typeof visible === "function") return undefined;
    if (typeof visible === "boolean") return visible;
    return visible == null ? undefined : !!visible;
  }

  /**
   * Persist column settings to localStorage (always when key is set).
   */
  private saveColumnSettingsLocal(
    config: { fields: Record<string, any>; limit?: number },
  ) {
    if (!this.settings.localStorageKey) return;
    try {
      localStorage[this.settings.localStorageKey] = JSON.stringify(config);
    } catch {
      /* ignore quota / private mode */
    }
  }

  /**
   * Parse localStorage column settings (supports legacy array format).
   */
  private parseLocalColumnSettings(raw: string): {
    fields?: Record<string, any>;
    limit?: number;
  } | null {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const fields: Record<string, any> = {};
        for (const item of parsed) {
          if (!item?.name) continue;
          fields[item.name] = {
            visible: item.visible,
            order: item.order,
            width: item.width,
            description: item.description,
          };
        }
        return Object.keys(fields).length ? { fields } : null;
      }
      if (parsed?.fields && typeof parsed.fields === "object") {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Save column settings to localStorage and/or backend
   */
  async saveColumnSettings() {
    const config: { fields: Record<string, any>; limit?: number } = {
      fields: {},
    };

    for (const key in this.store.state.fields) {
      const field = this.store.state.fields[key];
      if (field?.config) {
        const entry: Record<string, any> = {
          description: field.description,
          width: field.config.width,
          order: field.config.order,
        };
        const visible = this.serializeVisible(field.config.visible);
        if (visible !== undefined) {
          entry.visible = visible;
        }
        config.fields[key] = entry;
      }
    }

    const limit = Number(this.limit?.value ?? this.settings.limit);
    if (Number.isFinite(limit) && limit > 0) {
      config.limit = limit;
    }

    // Get settings key from settingsKey or store.name
    const settingsKey = this.getSettingsKey();

    // Always mirror to localStorage so remount works even if backend has no row
    this.saveColumnSettingsLocal(config);

    // Save to backend if settingsKey is available
    if (settingsKey) {
      try {
        const { tableSettingsApi } = await import("@/api/tableSettings");
        await tableSettingsApi.saveSettings(settingsKey, config);
        this.saveLimitLocal(settingsKey, config.limit);
      } catch (error) {
        console.error("Failed to save settings to backend:", error);
        this.saveLimitLocal(settingsKey, config.limit);
      }
    } else if (this.settings.localStorageKey) {
      this.saveLimitLocal(this.settings.localStorageKey, config.limit);
    } else {
      this.saveLimitLocal(this.store?.name, config.limit);
    }
  }

  private limitStorageKey(key: string | null | undefined): string | null {
    if (!key) return null;
    return `dbgrid-limit:${key}`;
  }

  private saveLimitLocal(key: string | null | undefined, limit?: number) {
    const storageKey = this.limitStorageKey(key);
    if (!storageKey || limit == null) return;
    try {
      localStorage.setItem(storageKey, String(limit));
    } catch {
      /* ignore quota / private mode */
    }
  }

  private loadLimitLocal(key: string | null | undefined): number | null {
    const storageKey = this.limitStorageKey(key);
    if (!storageKey) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw == null) return null;
      const limit = Number(raw);
      return Number.isFinite(limit) && limit > 0 ? limit : null;
    } catch {
      return null;
    }
  }

  /**
   * Load column settings from backend or localStorage
   * @returns Settings object or null
   */
  async loadColumnSettings(): Promise<{
    fields?: Record<string, any>;
    limit?: number;
  } | null> {
    // Get settings key from settingsKey or store.name
    const settingsKey = this.getSettingsKey();
    let loaded: { fields?: Record<string, any>; limit?: number } | null = null;

    const loadFromLocal = () => {
      if (
        !this.settings.localStorageKey ||
        !localStorage[this.settings.localStorageKey]
      ) {
        return null;
      }
      return this.parseLocalColumnSettings(
        localStorage[this.settings.localStorageKey],
      );
    };

    // Try to load from backend first if settingsKey is available
    if (settingsKey) {
      try {
        const { tableSettingsApi } = await import("@/api/tableSettings");
        const response: any = await tableSettingsApi.getSettings(settingsKey);
        // API возвращает: { complete: true, data: {...} | null }
        if (response?.complete && response?.data?.fields) {
          loaded = response.data;
        }
      } catch (error) {
        console.error("Failed to load settings from backend:", error);
      }
    }

    // Backend null/empty or no key — use localStorage (including after successful API with no row)
    if (!loaded?.fields) {
      loaded = loadFromLocal();
    }

    if (loaded?.limit == null) {
      const localLimit =
        this.loadLimitLocal(settingsKey) ??
        this.loadLimitLocal(this.settings.localStorageKey) ??
        this.loadLimitLocal(this.store?.name);
      if (localLimit != null) {
        loaded = { ...(loaded || {}), limit: localLimit };
      }
    }

    return loaded;
  }

  /**
   * Apply loaded settings to fields
   * @param settings - Settings object with fields
   */
  applyColumnSettings(settings: {
    fields?: Record<string, any>;
    limit?: number;
  }) {
    if (settings?.limit != null) {
      this.applySavedLimit(settings.limit);
    }

    if (!settings?.fields) return;

    for (const key in settings.fields) {
      const savedField = settings.fields[key];
      const field = this.store.state.fields[key];

      if (field && savedField && field.config) {
        if (savedField.width !== undefined) {
          // Нормализуем ширину: если число, добавляем "px"
          const width = savedField.width;
          field.config.width = typeof width === "number" ? `${width}px` : width;
        }
        if (savedField.order !== undefined) {
          field.config.order = savedField.order;
        }
        // Не затираем runtime-функции visible, если в сохранённых нет явного boolean
        if (typeof savedField.visible === "boolean") {
          field.config.visible = savedField.visible;
        }
      }
    }

    // В диалоге выбора не показываем технические id (настройки списка могут их вернуть)
    if (this.settings.slectMode && this.store.state.fields?.id?.config) {
      this.store.state.fields.id.config.visible = false;
    }
  }
}
